import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ShieldCheck, Eye, Scale, Target, FileText, Users, Ban } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const SectionCard = ({ icon, title, children, testid }) => (
  <section className="border rounded-xl p-6 md:p-8 shadow-sm" style={{ background: 'var(--bg-main)', borderColor: 'var(--border-soft)' }} data-testid={testid}>
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--bg-accent-soft)', color: 'var(--green-main)' }} aria-hidden="true">
        {icon}
      </div>
      <h2 className="text-lg md:text-xl font-black tracking-tight" style={{ color: 'var(--text-primary)' }}>{title}</h2>
    </div>
    <div className="text-[15px] leading-relaxed font-medium space-y-3" style={{ color: 'var(--text-secondary-strong)' }}>
      {children}
    </div>
  </section>
);

const Chip = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border" style={{ borderColor: 'var(--border-soft)', background: 'var(--bg-section)', color: 'var(--text-secondary-strong)' }}>
    {children}
  </span>
);

const AboutPage = () => {
  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-section)' }}>
      <Header />

      {/* Hero */}
      <section className="px-5 md:px-6 lg:px-12 py-14 md:py-20 border-b" style={{ background: 'var(--bg-main)', borderColor: 'var(--border-soft)' }} data-testid="about-hero">
        <div className="max-w-3xl mx-auto text-center">
          <span className="inline-block px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest mb-4" style={{ background: 'var(--bg-accent-soft)', color: 'var(--green-main)', letterSpacing: '0.08em' }}>
            HAKKIMIZDA
          </span>
          <h1 className="text-3xl md:text-5xl font-black tracking-tight mb-4" style={{ color: 'var(--text-primary)' }} data-testid="about-title">
            Bağımsız ürün doğrulama platformu
          </h1>
          <p className="text-base md:text-lg font-medium leading-relaxed" style={{ color: 'var(--text-secondary-strong)' }}>
            TestettiK®, ürünleri şeffaf şekilde test eden bağımsız bir platformdur. Amacımız, satın aldıktan sonra yaşanan hayal kırıklıklarını en baştan engellemektir.
          </p>
        </div>
      </section>

      {/* Content grid */}
      <div className="max-w-4xl mx-auto px-5 md:px-6 py-10 md:py-14 space-y-5">
        <SectionCard icon={<Target className="w-5 h-5" />} title="Misyonumuz" testid="about-mission">
          <p>Bu platformda gördüğünüz her ürün, gerçek bir test sürecinden geçmiştir. Kargo geldiğinde sürpriz yaşamamanız için buradayız.</p>
          <p style={{ color: 'var(--text-primary)', fontWeight: 700 }}>Biz sadece test ederiz ve sonucu olduğu gibi yayınlarız.</p>
        </SectionCard>

        <SectionCard icon={<Ban className="w-5 h-5" />} title="Kimiz — İlkelerimiz" testid="about-principles">
          <ul className="space-y-2.5">
            <li className="flex items-start gap-2.5"><CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--green-main)' }} /><span>Sponsorlu içerik üretmeyiz.</span></li>
            <li className="flex items-start gap-2.5"><CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--green-main)' }} /><span>Reklam karşılığı puan vermeyiz.</span></li>
            <li className="flex items-start gap-2.5"><CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--green-main)' }} /><span>Sonuçları değiştirmeyiz.</span></li>
          </ul>
        </SectionCard>

        <SectionCard icon={<Eye className="w-5 h-5" />} title="Test Metodolojimiz" testid="about-methodology">
          <p>Tüm ürünler aynı standart ile değerlendirilir:</p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-2">
            {[
              'Ürün fiziksel olarak incelenir',
              'Belirlenen kriterlere göre puanlanır',
              '0–10 arasında skor verilir',
              'Sonuç video ile birlikte yayınlanır',
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-2 border rounded-lg p-3" style={{ borderColor: 'var(--border-soft)', background: 'var(--bg-section)' }}>
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--green-main)' }} />
                <span className="text-sm">{t}</span>
              </li>
            ))}
          </ul>
        </SectionCard>

        <SectionCard icon={<Scale className="w-5 h-5" />} title="Puanlama Sistemi" testid="about-scoring">
          <p>Ürünler 0–10 arasında puanlanır.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            <div className="border rounded-lg p-4" style={{ borderColor: 'var(--border-soft)', background: 'var(--bg-section)' }}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-black px-2 py-0.5 rounded" style={{ background: '#1C3B2A', color: '#FFF' }}>7.0 ve üzeri</span>
                <span className="text-sm font-black" style={{ color: '#1C3B2A' }}>TEST BAŞARILI</span>
              </div>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Ürün belirlenen standartları karşılamıştır ve güvenle tercih edilebilir.</p>
            </div>
            <div className="border rounded-lg p-4" style={{ borderColor: 'var(--border-soft)', background: 'var(--bg-section)' }}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-black px-2 py-0.5 rounded" style={{ background: '#7A1F1F', color: '#FFF' }}>7.0 altı</span>
                <span className="text-sm font-black" style={{ color: '#A63333' }}>BEKLENTİYİ KARŞILAMADI</span>
              </div>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Ürün, belirlenen test kriterlerinin altında kalmıştır.</p>
            </div>
          </div>
        </SectionCard>

        <SectionCard icon={<ShieldCheck className="w-5 h-5" />} title="Bağımsızlık İlkelerimiz" testid="about-independence">
          <p>Test sonuçları hiçbir şekilde satın alınamaz.</p>
          <ul className="space-y-2.5 mt-2">
            {[
              'Sponsorlu içerik yayınlamayız',
              'Reklam karşılığı puan vermeyiz',
              'Test sonuçları değiştirilemez',
              'Ticari anlaşmalar test sonuçlarını etkilemez',
              'Yalnızca ürünü değerlendiririz, marka itibarı hakkında yorum yapmayız',
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: 'var(--green-main)' }} />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </SectionCard>

        <SectionCard icon={<Users className="w-5 h-5" />} title="Satıcılar İçin" testid="about-sellers">
          <p>Markalar ve satıcılar ürünlerini test edilmek üzere gönderebilir. Test sonucu ne olursa olsun yayınlanır.</p>
          <p><Chip>7.0 ve üzeri</Chip> puan alan ürünler, TestettiK® Güven Rozeti almaya hak kazanır.</p>
        </SectionCard>

        <SectionCard icon={<FileText className="w-5 h-5" />} title="Yasal Bilgilendirme" testid="about-legal">
          <p>Test süreci başladıktan sonra iptal edilemez ve ücret iadesi yapılmaz.</p>
          <p>TestettiK®, yalnızca ürün değerlendirmesi yapar ve doğrudan veya dolaylı zararlardan sorumlu tutulamaz.</p>
          <p>Başvuru sahibi, marka ve ürün adının TestettiK® tarafından kullanılmasına izin verir.</p>
        </SectionCard>

        {/* CTA */}
        <div className="rounded-xl p-8 md:p-10 text-center" style={{ background: 'linear-gradient(135deg, var(--green-main) 0%, var(--green-light) 100%)' }} data-testid="about-cta">
          <p className="text-base md:text-lg font-semibold text-white/90 mb-5">
            Test edilmiş ürünleri inceleyin ve güvenle alışveriş yapın
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/products"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#1C3B2A] px-8 py-3.5 text-sm font-bold rounded-lg hover:bg-neutral-100 transition"
              data-testid="about-cta-button">
              <CheckCircle2 className="w-5 h-5" />
              Test Edilmiş Ürünleri Gör
            </Link>
            <Link to="/contact"
              className="inline-flex items-center justify-center gap-2 border border-white/40 text-white px-8 py-3.5 text-sm font-bold rounded-lg hover:bg-white/10 transition">
              İletişime Geç
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AboutPage;
